import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { closeSnackbar } from 'notistack';
import useMst from '~/hooks/use_mst';
import toasts from '~/utils/toasts';
import useLocales from '~/hooks/use_locales';

const CHECK_NEW_RELEASE_INTERVAL = 60000;
const NEW_RELEASE_TOAST_KEY = 'new_release_available';

const useCheckRelease = () => {
  const { version } = useMst();
  const { t } = useLocales();
  const { pathname } = useLocation();
  const isOutdated = useRef(false);

  const notify = useCallback(() => {
    toasts.info(t('notifications.info.new_release_available'), {
      key: NEW_RELEASE_TOAST_KEY,
      persist: true,
      preventDuplicate: true,
      action: (key: string) => (
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            closeSnackbar(key);
            window.location.reload();
          }}
        >
          {t('base.buttons.refresh')}
        </Button>
      )
    });
  }, [t]);

  // Kept in a ref so a new `t` identity can't restart the polling interval
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled || isOutdated.current || document.hidden) {
        return;
      }
      const lastModified = await version.fetch();
      if (cancelled || !lastModified) {
        return;
      }
      if (!version.last_modified) {
        version.setLastModified(lastModified);
        return;
      }
      if (lastModified !== version.last_modified) {
        isOutdated.current = true;
        notifyRef.current();
      }
    };

    check();
    const interval = setInterval(check, CHECK_NEW_RELEASE_INTERVAL);
    // A tab coming back from the background is the most likely one to be stale
    document.addEventListener('visibilitychange', check);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
    };
  }, [version]);

  // Refresh a stale tab at a safe boundary — the next navigation — never mid-form
  useEffect(() => {
    if (isOutdated.current) {
      window.location.reload();
    }
  }, [pathname]);
};

export default useCheckRelease;
